import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import { mapProps } from 'recompose';
import { useSelector } from 'react-redux';

import CheckboxIcon from '../../../../../../../../components/icons/CheckboxIcon';
import Button from '../../../../../../../../components/global/Button';
import withModal from '../../../../../../../../components/global/modal/withModal';
import usePurchasesPendingConfiguration from '../hooks/usePurchasesPendingConfiguration';
import AlbumPrintPackageOptionList from './AlbumPrintPackageOptionList';
import ShoppingBasketIcon from '../../../../../../../../components/icons/ShoppingBasketIcon';
import PhotoPricingOptionList from './PhotoPricingOptionList';
import CustomPrintOptionList from './CustomPrintOptionList';
import useOpenModal from '../../../../../../../../hooks/useOpenModal';
import usePendingDigitalBundles from '../../DigitalBundlesMissingConfigurationNotice/hooks/usePendingDigitalBundles';
import { DIGITAL_BUNDLES_MISSING_CONFIGURATION_MODAL } from '../../DigitalBundlesMissingConfigurationNotice/components/DigitalBundlesMissingConfigurationModal';

export const PURCHASED_PRINT_PACKAGE_CONFIGURATION_COMPLETE_MODAL =
  'modal/PURCHASED_PRINT_PACKAGE_CONFIGURATION_COMPLETE';

const propTypes = {
  hierarchyItemId: PropTypes.string.isRequired,
  className: PropTypes.string,
  onClose: PropTypes.func,
  onCheckout: PropTypes.func,
};

const PurchasedPrintPackageConfigurationCompleteModal = ({
  hierarchyItemId,
  className,
  onClose,
  onCheckout,
}) => {
  const openModal = useOpenModal();

  const {
    albumMembershipGroup,
    albumPrintPackages,
    prepaidPrintPackagesMissingConfiguration,
    prepaidPhotoPricingsMissingConfiguration,
    prepaidCustomPrintsMissingConfiguration,
  } = usePurchasesPendingConfiguration({
    hierarchyItemId,
    shippableOnly: true,
  });

  const { activeIdentity } = useSelector(({ checkout }) => checkout);
  const { purchasedDigitalBundles } = usePendingDigitalBundles({
    identityId: activeIdentity?.id,
    albumFolderId: albumMembershipGroup?.is_album
      ? albumMembershipGroup?.album?.root_album_folder?.id
      : albumMembershipGroup?.album_folder?.id,
  });

  const itemsMissingConfigurationTotal =
    (prepaidPrintPackagesMissingConfiguration?.length ?? 0) +
    (prepaidPhotoPricingsMissingConfiguration?.length ?? 0) +
    (prepaidCustomPrintsMissingConfiguration?.length ?? 0);

  const classes = classNames({
    [className]: !!className,
  });

  return (
    <div className={classes}>
      <div className="d-flex align-items-center mb-2">
        <h3 className="w-100 text-center mb-0">
          Configuration Complete. You&apos;re almost done&hellip;
        </h3>
      </div>

      <div className="d-flex align-items-center">
        <span>
          <CheckboxIcon size={24} />
        </span>
        <p className="mb-0 ml-1 mb-2">
          Your prints are ready for shipping and have been added to your cart.
          {itemsMissingConfigurationTotal
            ? ' Please configure the rest of your items before checking out'
            : ' Please checkout to finalize your order'}
        </p>
      </div>
      {itemsMissingConfigurationTotal === 0 && (
        <Button
          btnBlock
          className="mb-2 btn-checkout-print-product-size-success"
          btnStyle="success-vibrant"
          onClick={() => {
            onCheckout();
            onClose();
          }}
        >
          <ShoppingBasketIcon />
          Checkout
        </Button>
      )}
      {purchasedDigitalBundles?.length > 0 && (
        <Button
          btnBlock
          btnOutline
          className="text-center mb-2"
          onClick={() => {
            openModal(DIGITAL_BUNDLES_MISSING_CONFIGURATION_MODAL, {
              successModalProps: {
                onCheckout,
              },
            });
            onClose();
          }}
        >
          Configure your Photo Bundles
        </Button>
      )}
      <Button
        btnBlock
        btnOutline
        className="text-center mb-2"
        onClick={onClose}
      >
        Continue shopping
      </Button>
      {itemsMissingConfigurationTotal > 0 && (
        <Fragment>
          <p className="text-danger text-sm text-bold mb-3">
            All pre-purchased prints & products must be configured before
            proceeding to checkout. Configure your remaining purchases (
            {itemsMissingConfigurationTotal}) to continue.
          </p>
          <p className="text-light text-uppercase text-extra-bold text-sm">
            Configure your remaining purchases:
          </p>
          <AlbumPrintPackageOptionList
            albumMembershipGroup={albumMembershipGroup}
            prepaidPrintPackages={prepaidPrintPackagesMissingConfiguration}
            albumPrintPackages={albumPrintPackages}
            hasPurchasedPhotoPricings={
              prepaidPhotoPricingsMissingConfiguration?.length > 0
            }
            onCheckout={onCheckout}
          />
          <PhotoPricingOptionList
            albumMembershipGroup={albumMembershipGroup}
            prepaidPhotoPricings={prepaidPhotoPricingsMissingConfiguration}
            onCheckout={onCheckout}
          />
          <CustomPrintOptionList
            albumMembershipGroup={albumMembershipGroup}
            prepaidCustomPrints={prepaidCustomPrintsMissingConfiguration}
            onCheckout={onCheckout}
          />
        </Fragment>
      )}
    </div>
  );
};

PurchasedPrintPackageConfigurationCompleteModal.propTypes = propTypes;

export default withModal(
  compose(
    mapProps(({ onRequestClose, actionProps = {}, ...props }) => ({
      onClose: onRequestClose,
      ...actionProps,
      ...props,
    })),
  )(PurchasedPrintPackageConfigurationCompleteModal),
  {
    name: PURCHASED_PRINT_PACKAGE_CONFIGURATION_COMPLETE_MODAL,
    size: 'sm',
    type: 'full',
    hideHeader: true,
    dismissible: false,
    shouldCloseOnOverlayClick: false,
  },
);
